import { useContext, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import Calendar from '@factorialco/f0-react/icons/app/Calendar'
import Phone from '@factorialco/f0-react/icons/app/Phone'
import { EnvironmentContext } from 'config/environment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { trackingEvents } from 'modules/core/lib/Tracking/events'
import { track } from 'modules/core/lib/Tracking/tracker'
import i18n from 'modules/core/lib/i18n'

import { ResourceHeaderProps } from 'modules/marketplace_modules/components/Revamp/types'
import { useMarketplaceUpsellRequest } from 'modules/marketplace_modules/hooks/useMarketplaceUpsellRequest'
import { useModuleStatus } from 'modules/marketplace_modules/hooks/useModuleStatus'
import { getButtonVisibility } from 'modules/marketplace_modules/lib/getButtonVisibility'
import { getInstalledStatus } from 'modules/marketplace_modules/lib/getInstalledStatus'
import { isValidModule } from 'modules/marketplace_modules/lib/typesValidation'
import CheckoutModal from 'modules/upsell/components/SelfUpsell/CheckoutModal'
import UpgradePlanModal from 'modules/upsell/components/SelfUpsell/UpgradePlanModal'
import { UpsellMeeting } from 'modules/upsell/components/UpsellMeeting'
import useBookMeeting from 'modules/upsell/hooks/useBookMeeting'
import useModuleDiscount from 'modules/upsell/hooks/useModuleDiscount'
import { SelfUpsellProvider } from 'modules/upsell/store/SelfUpsellProvider'
import { useLocalStorageWithExpiration } from 'modules/upselling_kit/utils/localStorage'

const RevampResourceHeader = ({
  addonReference,
  crossSellingAddonReference,
  mandatoryCrossSellingAddonReference,
  canSeeSelfUpsell,
  canSeeNormalUpsell,
  moduleData,
}: ResourceHeaderProps) => {
  const { ignoreUpsell } = useContext(EnvironmentContext)
  const { company } = useCurrentSession()
  const [isCheckoutModalOpen, setIsCheckoutModalOpen] = useState(false)
  const [isMeetingModalOpen, setIsMeetingModalOpen] = useState(false)
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false)
  const { pathname, search } = useLocation()
  const queryParams = new URLSearchParams(search)
  const actionParam = queryParams.get('action')
  const [storedUpsellOrigin] = useLocalStorageWithExpiration<{
    request_source: string
    request_source_category?: string
    request_source_component?: string
  }>('upsell_origin', 5)
  const upsellOrigin: string | undefined = storedUpsellOrigin?.request_source
  const upsellOriginCategory = storedUpsellOrigin?.request_source_category
  const upsellOriginComponent = storedUpsellOrigin?.request_source_component
  const currentSeats = company.subscription.currentCycleQuantity || company.subscription.seatCount
  const isLargeSegment = currentSeats >= 50

  // Prepare module includedInPlan data for bundle checking
  const moduleIncludedInPlan =
    moduleData?.includedInPlan && moduleData?.name
      ? [
          {
            moduleName: moduleData.name,
            includedInPlan: moduleData.includedInPlan,
          },
        ]
      : []

  // Hook that centralizes all queries needed for module status checking
  // Handles subscription items, subscription overloads, and module status map
  const {
    getModuleStatus,
    hasEnterpriseVariant,
    loading: moduleStatusLoading,
  } = useModuleStatus(addonReference.length > 0 ? [addonReference] : [], {})

  // Get complete module status (subscription + bundle + overload)
  const moduleStatus = getModuleStatus({
    moduleName: moduleData?.name || null,
    billingAddonReference: addonReference.length > 0 ? addonReference : null,
    moduleIncludedInPlan,
  })

  const isInstalled = moduleStatus.isInstalled
  const variant = moduleStatus.variant
  const installedAddonInternalId = moduleStatus.installedAddonInternalId

  const validChargebeeModuleName = isValidModule(moduleData?.chargebeeModuleName)
    ? moduleData?.chargebeeModuleName
    : undefined

  const {
    meetingLink,
    owner,
    loading: meetingLoading,
    error: meetingError,
  } = useBookMeeting({
    moduleRequested: validChargebeeModuleName || '',
  })
  const hasMeetingLink = !meetingLoading && !meetingError && meetingLink
  const { executeUpsellRequest, UpsellDialog } = useMarketplaceUpsellRequest({
    moduleSlug: moduleData?.slug || undefined,
    moduleRequested: validChargebeeModuleName,
    requestSource: upsellOrigin || 'Marketplace',
    pageSource: pathname,
    requestSourceCategory: upsellOriginCategory,
    requestSourceComponent: upsellOriginComponent,
    hubspotModuleName: moduleData?.hubspotModuleName || validChargebeeModuleName,
  })

  const { discount, discountLoading } = useModuleDiscount({
    moduleName: moduleData?.chargebeeModuleName || '',
    moduleSlug: moduleData?.slug || '',
  })

  const daysUntilDiscountExpires = discount?.disabledAt
    ? Math.max(
        0,
        Math.ceil(
          (new Date(discount.disabledAt).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
        )
      )
    : 0

  const handleOpenCheckoutModal = () => {
    setIsCheckoutModalOpen(true)
    track(trackingEvents.marketplaceModules.openCheckoutModal, {
      source: 'marketplace_resource_header_checkout_modal',
      module: moduleData?.slug,
      origin: window.location.href,
    })
  }

  const handleOpenUpgradeModal = () => {
    setIsUpgradeModalOpen(true)
    track(trackingEvents.marketplaceModules.openUpgradeModal, {
      source: 'marketplace_resource_header_upgrade_modal',
      module: moduleData?.slug,
      origin: window.location.href,
    })
  }

  const handleCloseCheckoutModal = () => {
    setIsCheckoutModalOpen(false)
  }

  const handleOpenMeetingModal = () => {
    setIsMeetingModalOpen(true)
  }

  const handleCloseMeetingModal = () => {
    setIsMeetingModalOpen(false)
  }
  const handleCloseUpgradeModal = () => {
    setIsUpgradeModalOpen(false)
  }

  const buttonVisibility = getButtonVisibility({
    isInstalled,
    variant,
    moduleStatus,
    hasEnterpriseVariant,
    canSeeSelfUpsell,
    canSeeNormalUpsell,
    ignoreUpsell,
    isLargeSegment,
    hasMeetingLink: !!hasMeetingLink,
    addonReference,
    moduleData,
  })

  const {
    showSelfUpsellButton,
    shouldShowUpgradeButton,
    shouldShowBookAMeetingButton,
    shouldShowRequestUpsellButtonPrimary,
    shouldShowRequestUpsellButtonSecondary,
  } = buttonVisibility

  const getPrimaryAction = () => {
    if (shouldShowUpgradeButton) {
      return {
        label: i18n.t('marketplace.revamp.upgrade_plan'),
        onClick: handleOpenUpgradeModal,
      }
    }

    if (showSelfUpsellButton) {
      return {
        label: i18n.t('marketplace.revamp.get_it_now'),
        onClick: handleOpenCheckoutModal,
      }
    }

    if (shouldShowRequestUpsellButtonPrimary) {
      return {
        label: i18n.t('upselling_kit.button.request_meeting'),
        onClick: executeUpsellRequest,
      }
    }

    return undefined
  }

  const getSecondaryActions = () => {
    if (shouldShowBookAMeetingButton) {
      return [
        {
          label: i18n.t('upsell.book_a_meeting'),
          onClick: handleOpenMeetingModal,
          icon: Calendar,
        },
      ]
    }
    if (shouldShowRequestUpsellButtonSecondary) {
      return [
        {
          label: i18n.t('upselling_kit.button.request_meeting'),
          onClick: executeUpsellRequest,
          icon: Phone,
        },
      ]
    }

    return undefined
  }

  useEffect(() => {
    if (shouldShowBookAMeetingButton) {
      track(trackingEvents.marketplaceModules.viewBookAMeetingButton, {
        module: moduleData?.slug,
        origin: window.location.href,
      })
    }
  }, [shouldShowBookAMeetingButton, moduleData?.slug])

  useEffect(() => {
    if (shouldShowRequestUpsellButtonPrimary) {
      track(trackingEvents.marketplaceModules.viewRequestAMeetingButton, {
        module: moduleData?.slug,
        origin: window.location.href,
        location: 'primary',
      })
    }
  }, [shouldShowRequestUpsellButtonPrimary, moduleData?.slug])

  useEffect(() => {
    if (shouldShowRequestUpsellButtonSecondary && !shouldShowBookAMeetingButton) {
      track(trackingEvents.marketplaceModules.viewRequestAMeetingButton, {
        module: moduleData?.slug,
        origin: window.location.href,
        location: 'secondary',
      })
    }
  }, [shouldShowRequestUpsellButtonSecondary, shouldShowBookAMeetingButton, moduleData?.slug])

  const shouldShowSelfUpsellProvider = canSeeSelfUpsell && addonReference?.length > 0

  useEffect(() => {
    if (actionParam === 'init-self-upsell' && shouldShowSelfUpsellProvider) {
      setIsCheckoutModalOpen(true)
    }
  }, [actionParam, shouldShowSelfUpsellProvider])

  const installedStatus = getInstalledStatus(
    isInstalled,
    variant,
    hasEnterpriseVariant,
    moduleStatus.from
  )

  const isLoadingActionsAndStatus = moduleStatusLoading || meetingLoading

  const resourceHeaderProps = {
    avatar: {
      type: 'generic' as const,
      name: moduleData?.slug || '',
      src: moduleData?.image?.url || '',
    },
    title: moduleData?.translatedName || '',
    description: moduleData?.shortDescription || '',
    primaryAction: isLoadingActionsAndStatus ? undefined : getPrimaryAction(),
    secondaryActions: isLoadingActionsAndStatus ? undefined : getSecondaryActions(),
    status: isLoadingActionsAndStatus
      ? undefined
      : {
          label: 'Status',
          text: installedStatus.label,
          variant: installedStatus.variant,
        },
    metadata: [
      ...(!discountLoading && discount && !isInstalled
        ? [
            {
              label: '',
              value: {
                type: 'status' as const,
                label: i18n.t('marketplace.revamp.discount', {
                  percentage: Math.floor(discount.percentage),
                  period: discount.period,
                  periodUnit: discount.period > 1 ? discount.periodUnit + 's' : discount.periodUnit,
                }),
                variant: 'warning' as const,
              },
            },
            {
              label: i18n.t('marketplace.revamp.discount_expires_in'),
              value: {
                content: i18n.t('marketplace.revamp.reminder_discount_days', {
                  days: daysUntilDiscountExpires,
                }),
                type: 'text' as const,
              },
            },
          ]
        : []),

      {
        label: i18n.t('marketplace.revamp.category'),
        value: {
          content: moduleData?.category?.title || '',
          type: 'text' as const,
        },
      },
    ],
  }

  const validModuleName = moduleData?.chargebeeModuleName && moduleData

  if (shouldShowSelfUpsellProvider) {
    return (
      <>
        <SelfUpsellProvider
          availableAddons={addonReference}
          availableCrossSellingAddons={crossSellingAddonReference}
          availableMandatoryCrossSellingAddons={mandatoryCrossSellingAddonReference}
          moduleName={moduleData?.chargebeeModuleName || ''}
        >
          <ResourceHeader {...resourceHeaderProps} />
          {validModuleName && isValidModule(moduleData.chargebeeModuleName) && (
            <CheckoutModal
              isOpen={isCheckoutModalOpen}
              onClose={handleCloseCheckoutModal}
              moduleName={moduleData.chargebeeModuleName}
              moduleSlug={moduleData.slug}
              hubspotModuleName={moduleData.hubspotModuleName}
              from={
                actionParam
                  ? `marketplace_resource_header_${actionParam}`
                  : 'marketplace_resource_header'
              }
            />
          )}

          {validModuleName && isValidModule(moduleData.chargebeeModuleName) && (
            <UpgradePlanModal
              installedAddonInternalId={installedAddonInternalId}
              isOpen={isUpgradeModalOpen}
              onClose={handleCloseUpgradeModal}
              moduleName={moduleData.chargebeeModuleName}
              hubspotModuleName={moduleData.hubspotModuleName}
              from={
                actionParam
                  ? `marketplace_resource_header_${actionParam}`
                  : 'marketplace_resource_header'
              }
            />
          )}
        </SelfUpsellProvider>
        <UpsellDialog />
        {meetingLink && (
          <UpsellMeeting
            isOpen={isMeetingModalOpen}
            onClose={handleCloseMeetingModal}
            meetingUrl={meetingLink}
            owner={owner}
            trackingSource="marketplace_resource_header_meeting_modal"
            moduleSlug={moduleData?.slug || undefined}
            moduleRequested={validChargebeeModuleName}
            requestSource={upsellOrigin || `Marketplace`}
            pageSource={pathname}
          />
        )}
      </>
    )
  }

  return (
    <>
      <ResourceHeader {...resourceHeaderProps} />
      <UpsellDialog />
      {meetingLink && (
        <UpsellMeeting
          isOpen={isMeetingModalOpen}
          onClose={handleCloseMeetingModal}
          meetingUrl={meetingLink}
          owner={owner}
          trackingSource="marketplace_resource_header_meeting_modal"
          moduleSlug={moduleData?.slug || undefined}
          moduleRequested={validChargebeeModuleName}
          requestSource={upsellOrigin || `Marketplace`}
          pageSource={pathname}
        />
      )}
    </>
  )
}

export default RevampResourceHeader
